import React from "react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signUp, getProfile } from "@/services/supabaseService";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function Register() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const normalizeEmail = (raw: string): string => {
    const trimmed = raw.trim();
    if (trimmed.includes("@") && !trimmed.includes(".")) {
      return trimmed + ".com";
    }
    return trimmed;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!name.trim() || !email.trim() || !phone.trim()) {
      setError("請填寫完整資料");
      return;
    }
    if (password.length < 6) {
      setError("密碼至少6位");
      return;
    }
    if (password !== confirmPwd) {
      setError("兩次密碼不一致");
      return;
    }
    if (!agree) {
      setError("請同意分銷協議");
      return;
    }

    setIsLoading(true);
    try {
      const data = await signUp(normalizeEmail(email), password, {
        name: name.trim(),
        phone: phone.trim(),
      });

      if (data?.user) {
        // Edge Function 註冊後已自動 signIn，session 已建立
        // 強制寫入 profile 到本地快取
        const profile = await getProfile(data.user.id);
        if (profile) {
          localStorage.setItem("jishi_auth_user", JSON.stringify(profile));
        }
        // 立即跳轉，不等待頁面刷新
        navigate("/dashboard", { replace: true });
        return;
      }

      // Fallback — 理論上不應該走到這裡
      setError("帳號已建立，請前往登入頁面登入");
    } catch (err: unknown) {
      let msg = "註冊失敗";
      if (err instanceof Error) {
        msg = err.message;
      } else if (typeof err === "object" && err !== null && "message" in err) {
        msg = String((err as Record<string, unknown>).message);
      } else if (typeof err === "string") {
        msg = err;
      }

      if (!msg || msg === "undefined" || msg === "null") {
        msg = "註冊失敗，請稍後再試";
      }

      // Map common Supabase errors to Chinese
      const lower = msg.toLowerCase();
      if (lower.includes("user already registered")) {
        msg = "此信箱已被註冊，請直接登入";
      } else if (lower.includes("invalid email")) {
        msg = "信箱格式不正確，請檢查後重試";
      } else if (lower.includes("password")) {
        msg = "密碼強度不足，請設定至少6位字元";
      } else if (lower.includes("row-level security") || lower.includes("42501")) {
        msg = "資料庫權限錯誤 (RLS)，請聯繫管理員";
      }

      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="pt-20 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-3">
              <img
                src="https://static.readdy.ai/image/823a8f074b021e82ba2884013917a5c5/a23db1fedd75cee41aae25a9154ab297.jpeg"
                alt="吉時國際貿易"
                className="h-10 w-auto object-contain rounded"
              />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">分銷商註冊</h1>
            <p className="text-sm text-gray-500">註冊後即可瀏覽全部認購價並下單</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-gray-600 mb-1.5 block">姓名</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="您的姓名"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1.5 block">郵箱</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1.5 block">電話</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="0912-345-678"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1.5 block">密碼</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="至少6位"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1.5 block">確認密碼</label>
              <input
                type="password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                placeholder="再次輸入密碼"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
              />
            </div>

            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
              />
              <span className="text-xs text-gray-500 leading-relaxed">
                我已閱讀並同意<a href="#" className="text-amber-600 hover:underline">《分銷商服務協議》</a>與<a href="#" className="text-amber-600 hover:underline">《隱私政策》</a>
              </span>
            </label>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-600">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 rounded-xl bg-amber-500 text-white font-medium text-sm hover:bg-amber-600 transition-all active:scale-[0.98] cursor-pointer disabled:opacity-50"
            >
              {isLoading ? "註冊中..." : "立即註冊"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              已有帳號？{" "}
              <Link to="/login" className="text-amber-600 font-medium hover:underline cursor-pointer">
                登入
              </Link>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}